<template>
  <div class="team-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="hero-background">
        <img 
          src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Team collaboration and business meeting"
          class="hero-image"
        />
        <div class="hero-overlay"></div>
      </div>
      <div class="hero-content">
        <div class="hero-text">
          <h1 class="hero-title">Our Team</h1>
          <p class="hero-subtitle">
            Meet the dedicated professionals behind Franchise Brigade
          </p>
        </div>
      </div>
    </section>

    <!-- Team Section -->
    <section class="team-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Meet Our Team</h2>
          <p class="section-subtitle">
            Experienced professionals dedicated to helping you find the perfect franchise match.
          </p>
        </div>

        <div v-if="teamLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading team members...</p>
        </div>
        
        <div v-else-if="teamStatus === 'failed'" class="error-state">
          <h3>Our team is assembling</h3>
          <p>Our amazing team members are getting ready to meet you! 👥</p>
          <button @click="fetchTeamMembers" class="retry-btn">Try Again</button>
        </div>
        
        <div v-else class="team-grid">
          <div 
            v-for="(member, index) in teamMembers" 
            :key="member.id || index"
            class="team-card"
          >
            <div class="team-image-container">
              <router-link :to="`/team/${member.id}`">
                <img 
                  :src="member.image" 
                  :alt="member.name" 
                  class="team-image"
                />
              </router-link>
            </div>
            <div class="team-info">
              <h3 class="team-name">{{ member.name }}</h3>
              <p class="team-position">{{ member.role }}</p>
              <p class="team-bio">{{ member.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">Ready to Start Your Franchise Journey?</h2>
          <p class="cta-subtitle">
            Whether you're looking to invest in a franchise or expand your brand through franchising, we're here to help.
          </p>
          <div class="cta-buttons">
            <router-link to="/franchises" class="cta-btn outline">
              Browse Franchises
            </router-link>
            <router-link to="/contactus" class="cta-btn primary">
              Contact Us
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const teamMembers = ref([])
const teamLoading = ref(true)
const teamStatus = ref('idle')

const fetchTeamMembers = async () => {
  teamLoading.value = true
  teamStatus.value = 'loading'
  
  try {
    const response = await fetch('https://www.franchisebrigade.com/admin/api/team')
    if (!response.ok) {
      throw new Error('Failed to fetch team members')
    }
    const data = await response.json()
    teamMembers.value = data.map(member => ({
      id: member.id,
      name: member.name,
      role: member.position,
      image: member.image,
      description: member.bio || 'Experienced professional dedicated to helping entrepreneurs find the perfect franchise match.'
    }))
    teamStatus.value = 'success'
  } catch (error) {
    teamStatus.value = 'failed'
    // Comprehensive fallback data with more team members
    teamMembers.value = [
      {
        id: 1,
        name: 'Sarah Johnson',
        role: 'CEO & Founder',
        image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Experienced entrepreneur with 15+ years in franchise consulting and business development.'
      },
      {
        id: 2,
        name: 'Michael Chen',
        role: 'Head of Operations',
        image: 'https://images.pexels.com/photos/3184296/pexels-photo-3184296.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Operations expert specializing in franchise development and scaling strategies.'
      },
      {
        id: 3,
        name: 'Emily Rodriguez',
        role: 'Senior Franchise Consultant',
        image: 'https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Dedicated to matching entrepreneurs with their perfect franchise opportunity.'
      },
      {
        id: 4,
        name: 'David Kim',
        role: 'Business Development Manager',
        image: 'https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Focused on expanding our network of quality franchise partners.'
      },
      {
        id: 5,
        name: 'Lisa Thompson',
        role: 'Marketing Director',
        image: 'https://images.pexels.com/photos/3184330/pexels-photo-3184330.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Expert in digital marketing and brand development for franchise businesses.'
      },
      {
        id: 6,
        name: 'James Wilson',
        role: 'Financial Advisor',
        image: 'https://images.pexels.com/photos/3184331/pexels-photo-3184331.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Specializes in franchise financing and investment planning for entrepreneurs.'
      },
      {
        id: 7,
        name: 'Maria Garcia',
        role: 'Legal Counsel',
        image: 'https://images.pexels.com/photos/3184332/pexels-photo-3184332.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Provides legal guidance on franchise agreements and compliance matters.'
      },
      {
        id: 8,
        name: 'Robert Taylor',
        role: 'Technology Lead',
        image: 'https://images.pexels.com/photos/3184333/pexels-photo-3184333.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Drives digital innovation and technology solutions for franchise operations.'
      },
      {
        id: 9,
        name: 'Jennifer Lee',
        role: 'Customer Success Manager',
        image: 'https://images.pexels.com/photos/3184334/pexels-photo-3184334.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Ensures exceptional support and success for our franchise partners.'
      },
      {
        id: 10,
        name: 'Alex Martinez',
        role: 'Regional Manager',
        image: 'https://images.pexels.com/photos/3184335/pexels-photo-3184335.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Manages regional franchise development and partner relationships.'
      },
      {
        id: 11,
        name: 'Rachel Green',
        role: 'Training Coordinator',
        image: 'https://images.pexels.com/photos/3184336/pexels-photo-3184336.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Develops comprehensive training programs for franchise partners.'
      },
      {
        id: 12,
        name: 'Thomas Brown',
        role: 'Quality Assurance Specialist',
        image: 'https://images.pexels.com/photos/3184337/pexels-photo-3184337.jpeg?auto=compress&cs=tinysrgb&w=400',
        description: 'Ensures high standards and quality control across all franchise operations.'
      }
    ]
  } finally {
    teamLoading.value = false
  }
}

onMounted(() => {
  fetchTeamMembers()
})
</script>

<style scoped>
.team-page {
  font-family: 'Inter', Arial, sans-serif;
  color: #333;
  background: #f8f9fa;
}

/* Hero Section */
.hero-section {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(30, 58, 138, 0.75);
  mix-blend-mode: multiply;
}

.hero-content {
  position: relative;
  z-index: 10;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  text-align: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  line-height: 1.1;
}

.hero-subtitle {
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
}

/* Team Section */
.team-section {
  padding: 5rem 0;
  background: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #1f2937;
}

.section-subtitle {
  font-size: 1.25rem;
  color: #6b7280;
  max-width: 48rem;
  margin: 0 auto;
  line-height: 1.6;
}

.loading-container {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

.loading-spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid #e5e7eb;
  border-top: 3px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #dc2626;
}

.retry-btn {
  background: var(--primary-blue);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #1d4ed8;
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.team-card {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  max-width: 320px;
  margin: 0 auto;
  width: 100%;
}

.team-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.team-image-container {
  height: 200px;
  overflow: hidden;
}

.team-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.team-card:hover .team-image {
  transform: scale(1.05);
}

.team-info {
  padding: 1.5rem;
}

.team-name {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.team-position {
  color: var(--primary-blue);
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.team-bio {
  color: #6b7280;
  line-height: 1.5;
  font-size: 0.85rem;
}

/* CTA Section */
.cta-section {
  padding: 4rem 0;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
}

.cta-content {
  text-align: center;
  max-width: 48rem;
  margin: 0 auto;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
}

.cta-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.cta-btn {
  padding: 0.875rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
  display: inline-block;
}

.cta-btn.outline {
  border: 2px solid white;
  color: white;
  background: transparent;
}

.cta-btn.outline:hover {
  background: white;
  color: var(--primary-blue);
}

.cta-btn.primary {
  background: white;
  color: var(--primary-blue);
}

.cta-btn.primary:hover {
  background: #f3f4f6;
  color: var(--primary-blue);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.25rem;
  }
  
  .section-title,
  .cta-title {
    font-size: 2rem;
  }
  
  .team-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
  }
  
  .team-card {
    max-width: 100%;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .hero-subtitle {
    font-size: 1.125rem;
  }
  
  .section-title,
  .cta-title {
    font-size: 1.75rem;
  }
  
  .section-subtitle,
  .cta-subtitle {
    font-size: 1.125rem;
  }
  
  .team-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .team-card {
    max-width: 100%;
  }
  
  .team-info {
    padding: 1rem;
  }
}
</style>
